"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { FormControl, FormLabel, Text, Input } from "@chakra-ui/react";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface BackchannelWordsProps {
  value: string[] | null | undefined;
  onChange: (value: string[] | null) => Promise<boolean> | void;
}

export const BackchannelWords: React.FC<BackchannelWordsProps> = ({
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Convert array to comma-separated string for the input
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setInputValue(value.join(", "));
    } else {
      setInputValue("");
    }
  }, [value]);

  const handleBlur = async () => {
    // Convert comma-separated string to array
    const words = inputValue
      .split(",")
      .map((word) => word.trim())
      .filter((word) => word.length > 0);

    const newValue = words.length > 0 ? words : null;

    // Only update if the value has changed
    if (JSON.stringify(newValue) !== JSON.stringify(value)) {
      setIsUpdating(true);
      try {
        await onChange(newValue);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <FormControl>
      <FormLabel
        fontSize="sm"
        fontWeight="medium"
        color={isDark ? "gray.300" : "gray.700"}
        mb={1}
      >
        Backchannel Words
      </FormLabel>
      <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"} mb={2}>
        A list of words that the agent would use for backchanneling.
      </Text>
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        placeholder="Split by comma. Example: yeah,uh-huh,okay"
        size="sm"
        bg={isDark ? "gray.700" : "white"}
        borderColor={isDark ? "gray.600" : "gray.300"}
        _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
        _focus={{
          borderColor: isDark ? "gray.500" : "gray.400",
          boxShadow: "none",
        }}
        isDisabled={isUpdating}
      />
    </FormControl>
  );
};
